/**
 * 动作面板（M4-04 / DESIGN.md#65-值得从-photoshop-补充的功能性价比排序）。
 *
 * 纯展示组件：动作列表进、意图出，不认识 store/CommandBus（与 LayersPanel/
 * AdjustmentPanel 同一条分层规则）。录制/回放/序列化的真正逻辑在
 * `app/actions/action-recorder.ts`；这里只负责把它摆成可点的列表。
 */
import type { JSX } from 'solid-js';
import { For, Show, createSignal } from 'solid-js';
import type { RecordedAction } from '../../app/actions/action-recorder';
import styles from './ActionsPanel.module.css';

export interface ActionsPanelProps {
  readonly isRecording: boolean;
  /** 录制中已捕获的步数，用于给用户一个"没白按"的即时反馈。 */
  readonly recordingStepCount: number;
  /** 上一次 stop() 因携带像素/子树而跳过的步数；null = 还没停过。 */
  readonly lastSkipped: number | null;
  readonly actions: readonly RecordedAction[];
  readonly onStartRecording: (name: string) => void;
  readonly onStopRecording: () => void;
  readonly onReplay: (action: RecordedAction) => void;
  readonly onSave: (action: RecordedAction) => void;
  readonly onLoad: () => void;
  readonly onRunBatch: (action: RecordedAction) => void;
  readonly onDelete: (name: string) => void;
}

export function ActionsPanel(props: ActionsPanelProps): JSX.Element {
  const [name, setName] = createSignal('新动作');

  return (
    <div class={styles.panel} data-testid="actions-panel">
      <div class={styles.recordRow}>
        <Show
          when={!props.isRecording}
          fallback={
            <>
              <span class={styles.recordingDot} aria-hidden="true" />
              <span class={styles.recordingLabel}>
                录制中…（已记录 {props.recordingStepCount} 步）
              </span>
              <button
                type="button"
                class={styles.stopButton}
                data-testid="actions-stop"
                onClick={() => props.onStopRecording()}
              >
                停止
              </button>
            </>
          }
        >
          <input
            class={styles.nameInput}
            type="text"
            value={name()}
            data-testid="actions-name-input"
            onInput={(event) => setName(event.currentTarget.value)}
          />
          <button
            type="button"
            class={styles.recordButton}
            data-testid="actions-record"
            onClick={() => props.onStartRecording(name())}
          >
            ● 录制
          </button>
        </Show>
      </div>
      <Show when={props.lastSkipped !== null && props.lastSkipped > 0}>
        <p class={styles.skippedHint}>
          有 {props.lastSkipped} 步因携带像素/图层结构未被录制（画笔描边、新建/删除图层等）。
        </p>
      </Show>

      <button type="button" class={styles.loadButton} data-testid="actions-load" onClick={() => props.onLoad()}>
        导入动作…
      </button>

      <ul class={styles.list} data-testid="actions-list">
        <For each={props.actions}>
          {(action) => (
            <li class={styles.item} data-testid={`action-item-${action.name}`}>
              <span class={styles.itemName}>{action.name}</span>
              <span class={styles.itemSteps}>{action.steps.length} 步</span>
              <div class={styles.itemButtons}>
                <button type="button" onClick={() => props.onReplay(action)}>
                  播放
                </button>
                <button type="button" onClick={() => props.onSave(action)}>
                  导出…
                </button>
                <button type="button" onClick={() => props.onRunBatch(action)}>
                  批处理…
                </button>
                <button type="button" onClick={() => props.onDelete(action.name)}>
                  删除
                </button>
              </div>
            </li>
          )}
        </For>
        <Show when={props.actions.length === 0}>
          <li class={styles.empty}>还没有录制或导入过动作</li>
        </Show>
      </ul>
    </div>
  );
}
